import React, { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, Building2 } from 'lucide-react';

const ProjectTeamEditor = ({ project, onChange }) => {
  const [newConsultant, setNewConsultant] = useState({
    name: '',
    company: '',
    role: '',
    phone: '',
    email: '',
    license_number: ''
  });
  
  const consultants = project.consultants || [];
  
  const roles = [
    'Architect',
    'Structural Engineer',
    'Mechanical Engineer',
    'Electrical Engineer',
    'Plumbing Engineer',
    'Civil Engineer',
    'Landscape Architect',
    'Food Service Consultant',
    'Acoustic Consultant',
    'Other'
  ];
  
  const handleAddConsultant = () => {
    if (!newConsultant.name || !newConsultant.role) {
      return;
    }
    
    onChange({
      ...project,
      consultants: [...consultants, { ...newConsultant, id: Date.now().toString() }]
    });
    
    setNewConsultant({
      name: '',
      company: '',
      role: '',
      phone: '',
      email: '',
      license_number: ''
    });
  };
  
  const handleRemoveConsultant = (id) => {
    onChange({
      ...project,
      consultants: consultants.filter(c => c.id !== id)
    });
  };
  
  const handleUpdateConsultant = (id, field, value) => {
    onChange({
      ...project,
      consultants: consultants.map(c =>
        c.id === id ? { ...c, [field]: value } : c
      )
    });
  };
  
  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">
          Project Team
        </h2>
        <p className="text-slate-600 dark:text-slate-400">
          Manage consultants and team members involved in this project
        </p>
      </div>
      
      {/* Existing Consultants */}
      {consultants.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
            Team Members ({consultants.length})
          </h3>
          
          {consultants.map((consultant) => (
            <Card key={consultant.id} className="p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                    <Building2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-slate-900 dark:text-white">
                      {consultant.name}
                    </h4>
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                      {consultant.role}
                    </p>
                  </div>
                </div>
                
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveConsultant(consultant.id)}
                  className="text-red-600 hover:text-red-700"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Company</Label>
                  <Input
                    value={consultant.company || ''}
                    onChange={(e) => handleUpdateConsultant(consultant.id, 'company', e.target.value)}
                    placeholder="Company name"
                  />
                </div>
                
                <div>
                  <Label>Phone</Label>
                  <Input
                    value={consultant.phone || ''}
                    onChange={(e) => handleUpdateConsultant(consultant.id, 'phone', e.target.value)}
                    placeholder="Phone number"
                  />
                </div>
                
                <div>
                  <Label>Email</Label>
                  <Input
                    value={consultant.email || ''}
                    onChange={(e) => handleUpdateConsultant(consultant.id, 'email', e.target.value)}
                    placeholder="email@example.com"
                  />
                </div>
                
                <div className="md:col-span-3">
                  <Label>License Number</Label>
                  <Input
                    value={consultant.license_number || ''}
                    onChange={(e) => handleUpdateConsultant(consultant.id, 'license_number', e.target.value)}
                    placeholder="Professional license number"
                  />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
      
      {/* Add New Consultant */}
      <Card className="p-6 border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800">
        <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">
          Add Team Member
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="new-name" className="text-slate-700 dark:text-slate-300">Name *</Label>
            <Input
              id="new-name"
              value={newConsultant.name}
              onChange={(e) => setNewConsultant({ ...newConsultant, name: e.target.value })}
              placeholder="John Doe"
              className="bg-white dark:bg-slate-900"
            />
          </div>
          
          <div>
            <Label htmlFor="new-role" className="text-slate-700 dark:text-slate-300">Role *</Label>
            <select
              id="new-role"
              value={newConsultant.role}
              onChange={(e) => setNewConsultant({ ...newConsultant, role: e.target.value })}
              className="flex h-10 w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm text-slate-900 dark:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <option value="">Select role...</option>
              {roles.map(role => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </div>
          
          <div>
            <Label htmlFor="new-company" className="text-slate-700 dark:text-slate-300">Company</Label>
            <Input
              id="new-company"
              value={newConsultant.company}
              onChange={(e) => setNewConsultant({ ...newConsultant, company: e.target.value })}
              placeholder="Company name"
              className="bg-white dark:bg-slate-900"
            />
          </div>
          
          <div>
            <Label htmlFor="new-phone" className="text-slate-700 dark:text-slate-300">Phone</Label>
            <Input
              id="new-phone"
              value={newConsultant.phone}
              onChange={(e) => setNewConsultant({ ...newConsultant, phone: e.target.value })}
              placeholder="Phone number"
              className="bg-white dark:bg-slate-900"
            />
          </div>
          
          <div>
            <Label htmlFor="new-email" className="text-slate-700 dark:text-slate-300">Email</Label>
            <Input
              id="new-email"
              type="email"
              value={newConsultant.email}
              onChange={(e) => setNewConsultant({ ...newConsultant, email: e.target.value })}
              placeholder="email@example.com"
              className="bg-white dark:bg-slate-900"
            />
          </div>
          
          <div>
            <Label htmlFor="new-license" className="text-slate-700 dark:text-slate-300">License Number</Label>
            <Input
              id="new-license"
              value={newConsultant.license_number}
              onChange={(e) => setNewConsultant({ ...newConsultant, license_number: e.target.value })}
              placeholder="License #"
              className="bg-white dark:bg-slate-900"
            />
          </div>
        </div>
        
        <Button
          onClick={handleAddConsultant}
          className="mt-4 bg-gradient-to-r from-blue-600 to-indigo-600"
          disabled={!newConsultant.name || !newConsultant.role}
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Team Member
        </Button>
      </Card>
    </div>
  );
};

export default ProjectTeamEditor;